import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { useSession } from '../store/session';
import { supabaseEnabled, signInWithGoogle, signOut, currentUser } from '../lib/supabase';

interface Props { 
  onOpenKeys(): void; 
  onOpenSavers(category?: string): void; 
  onOpenSocial(): void;
  onOpenStripe(): void;
  onOpenSaved(): void;
  onOpenNewChat(): void;
}

const DONATE = import.meta.env.VITE_DONATE_URL as string | undefined;

export default function Header({ onOpenKeys, onOpenSavers, onOpenSocial, onOpenStripe, onOpenSaved, onOpenNewChat }: Props) {
  const theme = useSession(s => s.settings.theme);
  const setSettings = useSession(s => s.setSettings);
  const location = useLocation();
  const onStudio = location.pathname.startsWith('/studio');
  const [user, setUser] = useState<any>(null);
  const [isLight, setIsLight] = useState(false);
  const [proActive, setProActive] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const categories = [
    'agents',
    'crypto',
    'characters',
    'scenes',
    'film',
    'code',
    'robotics',
    'games & worlds'
  ];

  useEffect(() => {
    function refreshPro() {
      setProActive(localStorage.getItem('protolive_pro_subscribed') === 'true');
    }
    refreshPro();
    window.addEventListener('protolive:pro_status_changed', refreshPro);
    return () => window.removeEventListener('protolive:pro_status_changed', refreshPro);
  }, []);

  useEffect(() => { 
    if (supabaseEnabled) {
      currentUser().then(setUser);
    } 
  }, []);

  useEffect(() => {
    const resolved = theme === 'light';
    setIsLight(resolved);
  }, [theme]);

  const cycle = () => {
    const next = theme === 'light' ? 'dark' : 'light';
    setSettings({ theme: next });
  };

  return (
    <header className="header glass glass--flat" style={{ padding: '6px 8px', gap: '4px', minHeight: '44px' }}>
      <div className="header__brand" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <div style={{ position: 'relative', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <AnimatePresence mode="popLayout" initial={false}>
            <motion.img 
              key={isLight ? 'light' : 'dark'}
              src={isLight 
                ? "https://theparadoxuniverse.b-cdn.net/%21File%20Organization/%21_Universal_Imagery/LOGOS/Logo_BLK_Transparent.png" 
                : "https://theparadoxuniverse.b-cdn.net/%21File%20Organization/%21_Universal_Imagery/LOGOS/Logo_White_Transparent.png"
              }
              alt="Logo" 
              referrerPolicy="no-referrer"
              initial={{ opacity: 0, scale: 0.8, rotate: -15 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              exit={{ opacity: 0, scale: 0.8, rotate: 15 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              style={{ position: 'absolute', height: '18px', width: '18px', objectFit: 'contain' }} 
            />
          </AnimatePresence>
        </div>
      </div >
      <span className="header__spacer" />
      <div className="header__mobile-sep" />

      {/* New Chat Button */}
      <button className="btn btn--primary" style={{ padding: '0 4px', minHeight: '24px', fontSize: '8.5px', gap: '1.5px', borderRadius: '4px' }} onClick={onOpenNewChat} title="New Chat">
        <PlusIcon /> <span className="header__btn-text">new</span>
      </button>

      {/* Theme Button */}
      <button className="btn btn--ghost" style={{ padding: '0 4px', minHeight: '24px', fontSize: '8.5px', gap: '1.5px' }} onClick={cycle} title={`Theme: ${theme}`}>
        <ThemeIcon theme={theme} /> <span className="header__btn-text">{theme}</span>
      </button>

      {/* Templates Button */}
      <button 
        className="btn btn--ghost" 
        style={{ padding: '0 4px', minHeight: '24px', fontSize: '8.5px', gap: '1.5px' }} 
        onClick={() => onOpenSavers()} 
        title="Templates Menu"
      >
        <TemplatesIcon /> 
        <span className="header__btn-text">templates</span>
      </button>

      {/* ProtoCut Studio (Left of Social) */}
      <Link
        to={onStudio ? '/' : '/studio'}
        className={`btn ${onStudio ? 'btn--primary' : 'btn--ghost'}`}
        style={{ padding: '0 4px', minHeight: '24px', fontSize: '8.5px', gap: '1.5px', textDecoration: 'none' }}
        title={onStudio ? 'Back to generator' : 'Open ProtoCut Studio video editor'}
      >
        <StudioIcon /> <span className="header__btn-text">{onStudio ? 'generator' : 'studio'}</span>
      </Link>

      {/* Social Community Business (Right of Templates, Left of Keys) */}
      <button className="btn btn--ghost" style={{ padding: '0 4px', minHeight: '24px', fontSize: '8.5px', gap: '1.5px' }} onClick={onOpenSocial} title="Community Social & Sharing">
        <SocialIcon /> <span className="header__btn-text">social</span>
      </button>
      
      {/* API Keys Button */}
      <button className="btn btn--ghost" style={{ padding: '0 4px', minHeight: '24px', fontSize: '8.5px', gap: '1.5px' }} onClick={onOpenKeys} title="API Keys">
        <KeysIcon /> <span className="header__btn-text">keys</span>
      </button>

      {/* Saved Drive Files Button */}
      <button className="btn btn--ghost" style={{ padding: '0 4px', minHeight: '24px', fontSize: '8.5px', gap: '1.5px' }} onClick={onOpenSaved} title="Saved Google Drive Files">
        <SavedIcon /> <span className="header__btn-text">saved</span>
      </button>

      {/* Stripe signup button - $5 full access */}
      <button className="btn btn--ghost" style={{ padding: '0 4px', minHeight: '24px', fontSize: '8.5px', gap: '1.5px', fontWeight: 'bold' }} onClick={onOpenStripe} title={proActive ? "Your Pro License is Active" : "Sign Up for Pro Access - $5"}>
        <StripeIcon /> <span className="header__btn-text">{proActive ? "★ PRO ACTIVE" : "PRO $5"}</span>
      </button>

      {/* Auth Google Sign In to the right of the key */}
      {supabaseEnabled && (
        user
          ? (
            <button className="btn btn--ghost" style={{ padding: '0 4px', minHeight: '24px', fontSize: '8.5px', gap: '1.5px' }} onClick={async () => { await signOut(); setUser(null); }} title={`Signed in as ${user.email}`}>
              <SignOutIcon /> <span className="header__btn-text">sign out</span>
            </button>
          )
          : (
            <button className="btn btn--primary" style={{ padding: '0 6px', minHeight: '24px', fontSize: '8.5px', gap: '1.5px' }} onClick={() => signInWithGoogle()} title="Google OAuth Authentication">
              <SignInIcon /> <span className="header__btn-text">sign in</span>
            </button>
          )
      )}

      {DONATE && (
        <a className="btn btn--icon" style={{ width: '24px', height: '24px' }} href={DONATE} target="_blank" rel="noreferrer" title="donate">
          <DonateSVG />
        </a>
      )}
    </header>
  );
}

function SavedIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
      <polyline points="17 21 17 13 7 13 7 21" />
      <polyline points="7 3 7 8 15 8" />
    </svg>
  );
}

function TemplatesIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect width="7" height="9" x="3" y="3" rx="1" />
      <rect width="7" height="5" x="14" y="3" rx="1" />
      <rect width="7" height="9" x="14" y="12" rx="1" />
      <rect width="7" height="5" x="3" y="15" rx="1" />
    </svg>
  );
}

function StudioIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="6" width="14" height="12" rx="2" />
      <path d="m22 8-6 4 6 4V8z" />
    </svg>
  );
}

function SocialIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function KeysIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="7.5" cy="15.5" r="5.5" />
      <path d="m21 2-9.6 9.6M15.5 7.5l3 3M20 3l-3 3" />
    </svg>
  );
}

function StripeIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <line x1="2" y1="10" x2="22" y2="10" />
    </svg>
  );
}

function SignInIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
      <polyline points="10 17 15 12 10 7" />
      <line x1="15" y1="12" x2="3" y2="12" />
    </svg>
  );
}

function SignOutIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function ThemeIcon({ theme }: { theme: string }) {
  if (theme === 'light') return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="4"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M5 19l2-2M17 7l2-2"/></svg>;
  return <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M21 12.8A9 9 0 1111.2 3a7 7 0 009.8 9.8z"/></svg>;
}

function DonateSVG() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 10-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
    </svg>
  );
}
